import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Check, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "bg-brand-ink border-white/10 focus:border-brand-gold focus-visible:ring-brand-gold/30 text-white placeholder:text-white/40 rounded-sm h-12";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    postcode: "",
    service: "",
    project_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !form.project_type) {
      toast.error("Please fill in the required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Quote request sent — we’ll be in touch within 48 hours.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't send right now. Please call us.";
      toast.error(typeof msg === "string" ? msg : "Couldn't send right now. Please call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-brand-surface border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-brand-gold/90">
            <span className="inline-block h-px w-10 bg-brand-gold" />
            Free quote · 48-hour reply
          </div>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl text-white leading-tight text-balance">
            Tell us about the{" "}
            <span className="text-brand-gold italic">job</span>.
          </h2>
          <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed">
            A few details is all we need. We&apos;ll come back within 48 hours with
            an honest, itemised quote — no pressure, no jargon.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href={`tel:${SITE.phoneRaw}`}
              data-testid="contact-phone-link"
              className="flex items-start gap-4 group"
            >
              <span className="h-11 w-11 rounded-sm border border-brand-gold/30 inline-flex items-center justify-center text-brand-gold group-hover:bg-brand-gold group-hover:text-black transition-colors">
                <Phone className="h-4 w-4" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-white/50">
                  Call us
                </div>
                <div className="text-lg text-white group-hover:text-brand-gold transition-colors">
                  {SITE.phone}
                </div>
              </div>
            </a>

            <a
              href={`mailto:${SITE.email}`}
              data-testid="contact-email-link"
              className="flex items-start gap-4 group"
            >
              <span className="h-11 w-11 rounded-sm border border-brand-gold/30 inline-flex items-center justify-center text-brand-gold group-hover:bg-brand-gold group-hover:text-black transition-colors">
                <Mail className="h-4 w-4" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-white/50">
                  Email
                </div>
                <div className="text-lg text-white group-hover:text-brand-gold transition-colors break-all">
                  {SITE.email}
                </div>
              </div>
            </a>

            <div className="flex items-start gap-4">
              <span className="h-11 w-11 rounded-sm border border-brand-gold/30 inline-flex items-center justify-center text-brand-gold">
                <MapPin className="h-4 w-4" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-white/50">
                  Service area
                </div>
                <div className="text-lg text-white">{SITE.area}</div>
                <div className="text-sm text-white/60 mt-1">
                  Winchester · Southampton · Portsmouth · Basingstoke ·
                  Andover · Guildford · Reading · Bournemouth and surrounds
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-quote-form"
            className="bg-brand-card border border-white/5 rounded-sm p-6 md:p-10"
          >
            {done ? (
              <div
                data-testid="contact-success"
                className="text-center py-12"
              >
                <div className="mx-auto h-14 w-14 rounded-full border border-brand-gold/40 inline-flex items-center justify-center text-brand-gold">
                  <Check className="h-6 w-6" />
                </div>
                <h3 className="mt-6 font-serif text-3xl md:text-4xl text-white">
                  Thanks — we&apos;ve got it.
                </h3>
                <p className="mt-4 text-white/70 max-w-md mx-auto">
                  We&apos;ll review your job and come back to you within 48 hours
                  with a clear, itemised quote. For anything urgent, give Luke
                  a ring on{" "}
                  <a
                    href={`tel:${SITE.phoneRaw}`}
                    className="text-brand-gold underline"
                  >
                    {SITE.phone}
                  </a>
                  .
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-1">
                  <Label htmlFor="name" className="text-white/80 text-xs uppercase tracking-[0.18em]">
                    Your name *
                  </Label>
                  <Input
                    id="name"
                    data-testid="form-name-input"
                    className={`mt-2 ${inputCls}`}
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Doe"
                    required
                  />
                </div>
                <div className="sm:col-span-1">
                  <Label htmlFor="phone" className="text-white/80 text-xs uppercase tracking-[0.18em]">
                    Phone *
                  </Label>
                  <Input
                    id="phone"
                    data-testid="form-phone-input"
                    className={`mt-2 ${inputCls}`}
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="07000 000000"
                    required
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="email" className="text-white/80 text-xs uppercase tracking-[0.18em]">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="form-email-input"
                    className={`mt-2 ${inputCls}`}
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    required
                  />
                </div>
                <div className="sm:col-span-1">
                  <Label className="text-white/80 text-xs uppercase tracking-[0.18em]">
                    Service *
                  </Label>
                  <Select
                    value={form.service}
                    onValueChange={(v) => setForm((p) => ({ ...p, service: v }))}
                  >
                    <SelectTrigger
                      data-testid="form-service-select"
                      className={`mt-2 ${inputCls}`}
                    >
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-brand-card border-white/10 text-white">
                      <SelectItem value="Rendering">Rendering</SelectItem>
                      <SelectItem value="Plastering">Plastering</SelectItem>
                      <SelectItem value="Drylining">Drylining</SelectItem>
                      <SelectItem value="Not sure">Not sure yet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-1">
                  <Label className="text-white/80 text-xs uppercase tracking-[0.18em]">
                    Project type *
                  </Label>
                  <Select
                    value={form.project_type}
                    onValueChange={(v) => setForm((p) => ({ ...p, project_type: v }))}
                  >
                    <SelectTrigger
                      data-testid="form-project-type-select"
                      className={`mt-2 ${inputCls}`}
                    >
                      <SelectValue placeholder="Domestic or commercial" />
                    </SelectTrigger>
                    <SelectContent className="bg-brand-card border-white/10 text-white">
                      <SelectItem value="Domestic">Domestic</SelectItem>
                      <SelectItem value="Commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="postcode" className="text-white/80 text-xs uppercase tracking-[0.18em]">
                    Postcode
                  </Label>
                  <Input
                    id="postcode"
                    data-testid="form-postcode-input"
                    className={`mt-2 ${inputCls}`}
                    value={form.postcode}
                    onChange={update("postcode")}
                    placeholder="SO23 0AA"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="message" className="text-white/80 text-xs uppercase tracking-[0.18em]">
                    Tell us about the job
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="form-message-input"
                    rows={4}
                    className="mt-2 bg-brand-ink border-white/10 focus:border-brand-gold focus-visible:ring-brand-gold/30 text-white placeholder:text-white/40 rounded-sm"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Approx area, timescale, any photos you can email separately…"
                  />
                </div>
                <div className="sm:col-span-2 pt-2">
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="form-submit-button"
                    className="bg-brand-gold hover:bg-brand-goldDark text-black font-semibold tracking-wide rounded-sm w-full py-6 group"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Send my quote request
                        <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </Button>
                  <p className="mt-3 text-xs text-white/50 text-center">
                    By submitting you agree we can contact you about your enquiry. We never share your details.
                  </p>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};
